import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import styles from './WaitlistGate.module.css';

interface WaitlistGateProps {
  children: React.ReactNode;
}

/**
 * Wraps protected content. Enforces two checks in order:
 *   1. User must be authenticated — otherwise prompts login.
 *   2. User must have a redeemed (and unexpired) invite code — otherwise
 *      shows the code-entry form.
 */
export const WaitlistGate: React.FC<WaitlistGateProps> = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    error: authError,
    waitlistApproved,
    submitInviteCode,
    user,
    logout,
    loginWithRedirect,
  } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Auth still resolving — render nothing to avoid flicker
  if (isLoading) return null;

  // Logged in at Auth0 but the app session could not be established
  // (token or profile fetch failed). Surface the real reason and let the
  // user retry, instead of silently failing on the next API call.
  if (!isAuthenticated && user && authError) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h2 className={styles.title}>Couldn't complete sign-in</h2>
          <p className={styles.subtitle}>{authError.message}</p>
          <button
            type="button"
            className={styles.button}
            onClick={() => loginWithRedirect()}
          >
            Try again
          </button>
          <a href="/home" className={styles.homeLink}>
            Go to homepage
          </a>
          <button className={styles.logoutLink} onClick={() => logout()}>
            Log out
          </button>
        </div>
      </div>
    );
  }

  // Not authenticated — show login prompt
  if (!isAuthenticated) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h2 className={styles.title}>Please log in</h2>
          <p className={styles.subtitle}>
            You need to be logged in to access this page.
          </p>
          <button
            type="button"
            className={styles.button}
            onClick={() => loginWithRedirect()}
          >
            Log in
          </button>
          <a href="/home" className={styles.homeLink}>
            Go to homepage
          </a>
        </div>
      </div>
    );
  }

  // Already approved — render children
  if (waitlistApproved) return <>{children}</>;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    const ok = await submitInviteCode(code.trim());
    setLoading(false);
    if (!ok) setError('Invalid code. Please check and try again.');
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h2 className={styles.title}>You're on the waitlist</h2>
        <p className={styles.subtitle}>
          Hi {user?.name || user?.email}! We're reviewing your access.
          Once approved you'll receive a 6-character invite code via email.
        </p>

        <form onSubmit={handleSubmit} className={styles.form}>
          <label htmlFor="invite-code" className={styles.label}>
            Enter your invite code
          </label>
          <input
            id="invite-code"
            className={styles.input}
            type="text"
            maxLength={6}
            placeholder="e.g. AB12CD"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            autoComplete="off"
            autoFocus
          />
          {error && <p className={styles.error}>{error}</p>}
          <button
            type="submit"
            className={styles.button}
            disabled={loading || code.length < 6}
          >
            {loading ? 'Verifying…' : 'Submit'}
          </button>
        </form>

        <a href="/home" className={styles.homeLink}>
          Go to homepage
        </a>
        <button className={styles.logoutLink} onClick={() => logout()}>
          Log out
        </button>
      </div>
    </div>
  );
};
